import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, Select, Switch,
    Space, Tag, Typography, message, Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;

const AdminSubjectManager = () => {
    const [subjects, setSubjects] = useState([]);
    const [subjectTypes, setSubjectTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterTypeId, setFilterTypeId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const fetchData = useCallback(async (typeId = null) => {
        setLoading(true);
        try {
            const params = { all: 1 };
            if (typeId) params.subject_type_id = typeId;

            const [subjectRes, typeRes] = await Promise.all([
                axiosInstance.get('/api/v1/subjects', { params }),
                axiosInstance.get('/api/v1/subject-types'),
            ]);

            setSubjects(subjectRes.data?.data ?? []);
            setSubjectTypes(typeRes.data?.data ?? []);
        } catch {
            message.error('Không thể tải dữ liệu ngành học.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(filterTypeId); }, [fetchData, filterTypeId]);

    const openAdd = () => {
        setEditRecord(null);
        form.resetFields();
        form.setFieldsValue({ status: true });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditRecord(record);
        form.setFieldsValue({
            code: record.code,
            name: record.name,
            subject_type_id: record.subject_type_id,
            status: record.status,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            if (editRecord) {
                await axiosInstance.put(`/api/v1/admin/subjects/${editRecord.id}`, values);
                message.success('Đã cập nhật ngành học.');
            } else {
                await axiosInstance.post('/api/v1/admin/subjects', values);
                message.success('Đã thêm ngành học.');
            }

            setModalOpen(false);
            fetchData(filterTypeId);
        } catch (err) {
            if (err?.errorFields) return;
            const msg = err.response?.data?.errors
                ? Object.values(err.response.data.errors).flat().join(', ')
                : 'Lưu thất bại. Vui lòng thử lại.';
            message.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/api/v1/admin/subjects/${id}`);
            message.success('Đã xóa ngành học.');
            fetchData(filterTypeId);
        } catch {
            message.error('Xóa thất bại.');
        }
    };

    const columns = [
        {
            title: 'STT',
            width: 55,
            align: 'center',
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Mã ngành',
            dataIndex: 'code',
            width: 80,
            render: (v) => <Text code>{v}</Text>,
        },
        {
            title: 'Tên ngành',
            dataIndex: 'name',
            width: 300,
            ellipsis: true,
        },
        {
            title: 'Loại ngành',
            width: 250,
            render: (_, r) =>
                r.subject_type
                    ? <Tag color="blue">{r.subject_type.name}</Tag>
                    : <Text type="secondary">—</Text>,
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            width: 80,
            align: 'center',
            render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Hoạt động' : 'Không hoạt động'}</Tag>,
        },
        {
            title: '',
            width: 90,
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    <Popconfirm
                        title="Xác nhận xóa ngành này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Xóa" cancelText="Hủy"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <AdminLayout title="Quản lý ngành học">
            <Space style={{ marginBottom: 16 }} wrap>
                <Select
                    placeholder="Lọc theo loại ngành"
                    allowClear
                    style={{ width: 200 }}
                    value={filterTypeId}
                    onChange={(v) => setFilterTypeId(v ?? null)}
                    options={subjectTypes.map(t => ({ value: t.id, label: t.name }))}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                    Thêm ngành
                </Button>
                {subjects.length > 0 && (
                    <Text type="secondary">{subjects.length} ngành</Text>
                )}
            </Space>

            <Table
                columns={columns}
                dataSource={subjects}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{ pageSize: 25, showTotal: (t) => `Tổng ${t} bản ghi` }}
            />

            <Modal
                title={editRecord ? 'Sửa ngành học' : 'Thêm ngành học'}
                open={modalOpen}
                onOk={handleSave}
                onCancel={() => setModalOpen(false)}
                confirmLoading={saving}
                okText={editRecord ? 'Cập nhật' : 'Thêm'}
                cancelText="Hủy"
                destroyOnClose
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="code"
                        label="Mã ngành"
                        rules={[
                            { required: true, message: 'Vui lòng nhập mã ngành.' },
                            { max: 20, message: 'Mã ngành tối đa 20 ký tự.' },
                        ]}
                    >
                        <Input placeholder="VD: 7480201" />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="Tên ngành"
                        rules={[{ required: true, message: 'Vui lòng nhập tên ngành.' }]}
                    >
                        <Input placeholder="VD: Công nghệ thông tin" />
                    </Form.Item>
                    <Form.Item name="subject_type_id" label="Loại ngành">
                        <Select
                            allowClear
                            placeholder="Chọn loại ngành (tuỳ chọn)"
                            options={subjectTypes.map(t => ({ value: t.id, label: t.name }))}
                        />
                    </Form.Item>
                    <Form.Item name="status" label="Trạng thái" valuePropName="checked">
                        <Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
                    </Form.Item>
                </Form>
            </Modal>
        </AdminLayout>
    );
};

export default AdminSubjectManager;
